import React, { useState } from 'react';
import { ToggleSwitch, CollapseTransition } from '../../sdk';

/**
 * Bullets/list editor with toggle and reordering (same structure as Tags)
 * @param {Object} props
 * @param {boolean} props.enabled - Whether bullets are enabled
 * @param {function} props.onToggle - Callback when toggle changes
 * @param {string[]} props.bullets - Current bullets array
 * @param {function} props.onChange - Callback when bullets change
 * @param {number} props.maxBullets - Maximum number of bullets (default: 5)
 */
export function BulletsEditor({
  enabled,
  onToggle,
  bullets = [],
  onChange,
  maxBullets = 5,
}) {
  const [bulletInput, setBulletInput] = useState('');

  const addBullet = () => {
    const b = bulletInput.trim();
    if (!b) return;
    if (bullets.length >= maxBullets) return;
    onChange([...bullets, b]);
    setBulletInput('');
  };

  const removeBullet = (index) => {
    const updated = [...bullets];
    updated.splice(index, 1);
    onChange(updated);
  };

  const updateBullet = (index, value) => {
    const updated = [...bullets];
    updated[index] = value;
    onChange(updated);
  };

  const moveBullet = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= bullets.length) return;
    
    const updated = [...bullets];
    const [removed] = updated.splice(index, 1);
    updated.splice(newIndex, 0, removed);
    onChange(updated);
  };

  return (
    <div className="field-container">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="module-header">Bullets</div>
          <div className="module-desc">
            Unordered list (up to {maxBullets} items).
          </div>
        </div>
        <ToggleSwitch
          checked={enabled}
          onChange={(checked) => onToggle(checked)}
          size="sm"
        />
      </div>

      <CollapseTransition open={enabled}>
        <>
          <div className="mt-3 flex gap-2">
            <input
              value={bulletInput}
              onChange={(e) => setBulletInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addBullet();
                }
              }}
              className="input-base"
              placeholder="Add a bullet point"
            />
            <button 
              onClick={addBullet} 
              className="btn-control"
              disabled={bullets.length >= maxBullets}
            >
              Add
            </button>
          </div>

          {bullets.length > 0 && (
            <div className="mt-3 space-y-2">
              {bullets.map((b, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    value={b}
                    onChange={(e) => updateBullet(i, e.target.value)}
                    className="input-base flex-1"
                    placeholder="Bullet text"
                  />
                  <button
                    onClick={() => moveBullet(i, -1)}
                    disabled={i === 0}
                    className="icon-btn"
                    title="Move up"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => moveBullet(i, 1)}
                    disabled={i === bullets.length - 1}
                    className="icon-btn"
                    title="Move down"
                  >
                    ↓
                  </button>
                  <button
                    onClick={() => removeBullet(i)}
                    className="icon-btn"
                    title="Remove bullet"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      </CollapseTransition>
    </div>
  );
}
